import { useRef, useState } from 'react';
import { ApiFailure, tokens } from '../lib/api';

interface Plan {
  dryRun: boolean;
  rows: number;
  newZones: string[];
  newSuburbs: string[];
  updatedSuburbs: string[];
  newKeywords: number;
  pricingRules: number;
  problems: Array<{ line: number; text: string; reason: string }>;
  notes: string[];
}

/**
 * 按 Excel 导入区域价目表。
 *
 * ★ 一个城市几百个郊区,在界面上一个一个加是**不会发生的事**:
 *   建区域 → 加关键词 → 填加价,三步 × 三百次。
 *   实际结果是只配了三五个区,剩下的单全落进「匹配不上」。
 *
 * ★ 一定要先**试算**再落库。它一次改掉几百条匹配规则,
 *   而算错价要等到客户拿账单来对的时候才发现。
 */
export function ZonePricingImport({ onApplied }: { onApplied: () => Promise<void> }) {
  const [plan, setPlan] = useState<Plan | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  async function send(f: File, apply: boolean) {
    setBusy(true);
    setErr(null);
    try {
      const body = new FormData();
      body.append('file', f);
      const t = tokens.get();
      const res = await fetch(`/api/v1/zone-pricing/import?apply=${apply}`, {
        method: 'POST',
        headers: t ? { Authorization: `Bearer ${t.accessToken}` } : {},
        body,
      });
      const json = await res.json();
      if (!res.ok) throw new ApiFailure(res.status, json.error);
      setPlan(json as Plan);
      if (apply) await onApplied();
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '导入失败');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      aria-label="区域价目表导入"
      className="mb-4 rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700
                 dark:bg-slate-800"
    >
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
          按 Excel 导入区域和加价
        </span>
        <a
          href="/api/v1/zone-pricing/template"
          onClick={async (e) => {
            /*
             * ★ 下载要带上登录凭证 —— 直接 <a href> 的话浏览器不带 Authorization 头,
             *   拿回来的是一个 401 的 JSON,而它会被存成一个叫 template.xlsx 的
             *   坏文件:双击打不开,而人以为是我们的模板有问题。
             */
            e.preventDefault();
            const t = tokens.get();
            const res = await fetch('/api/v1/zone-pricing/template', {
              headers: t ? { Authorization: `Bearer ${t.accessToken}` } : {},
            });
            if (!res.ok) {
              setErr('模板下载失败');
              return;
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'zone-pricing-template.xlsx';
            a.click();
            URL.revokeObjectURL(url);
          }}
          className="text-sm text-brand-600 hover:underline dark:text-brand-400"
        >
          下载模板
        </a>
        <input
          ref={input}
          type="file"
          aria-label="区域价目表文件"
          accept=".xlsx,.csv"
          onChange={(e) => {
            const f = e.target.files?.[0] ?? null;
            setFile(f);
            setPlan(null);
            if (f) void send(f, false);
          }}
          className="text-sm text-slate-500"
        />
      </div>
      <p className="mt-1 text-xs text-slate-400">
        列:City / District / Suburb / Price。大区只在换行时写一次,下面留空表示沿用
      </p>

      {err && <div className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}

      {plan && (
        <div className="mt-3 rounded-lg bg-slate-50 p-3 text-sm dark:bg-slate-900/50">
          <div className="text-slate-700 dark:text-slate-200">
            {plan.dryRun ? '试算结果(还没写库)' : '已导入'}:共 {plan.rows} 行 · 新建大区{' '}
            {plan.newZones.length} 个 · 新建郊区 {plan.newSuburbs.length} 个 · 关键词{' '}
            {plan.newKeywords} 条
            {plan.updatedSuburbs.length > 0 && ` · 改了加价 ${plan.updatedSuburbs.length} 个`}
          </div>

          {plan.problems.length > 0 && (
            <div className="mt-2">
              {/* ★ 有问题的行要列出来,而不是只说「N 行失败」—— 人要知道去改哪一行 */}
              <div className="text-amber-700 dark:text-amber-400">
                有 {plan.problems.length} 行没导进去:
              </div>
              <ul className="mt-1 space-y-0.5 text-xs text-slate-500">
                {plan.problems.slice(0, 8).map((p, i) => (
                  <li key={`${p.line}-${i}`}>
                    第 {p.line} 行「{p.text}」—— {p.reason}
                  </li>
                ))}
                {plan.problems.length > 8 && <li>…还有 {plan.problems.length - 8} 行</li>}
              </ul>
            </div>
          )}

          {plan.notes.map((n, i) => (
            <div key={i} className="mt-1 text-xs text-slate-500">
              · {n}
            </div>
          ))}

          {plan.dryRun && plan.rows > 0 && (
            <button
              onClick={() => file && void send(file, true)}
              disabled={busy || !file}
              className="mt-3 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white
                         disabled:opacity-40"
            >
              {busy ? '导入中…' : '确认导入'}
            </button>
          )}
        </div>
      )}
    </section>
  );
}
